import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import { Alert, Box, Button, Chip, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import { TenantSettingsContext } from '../../../App'
import { vatMultiplierForItem } from '../../../helpers/region'

const lineItemsTotal = (items, region) =>
  (items || []).reduce((total, item) => {
    const vatMultiplier = vatMultiplierForItem(item.vat, region)
    return total + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) * vatMultiplier
  }, 0)

// What's actually left to pay: VAT-inclusive gross, net of credit notes,
// the flat discount, and payments already made
const getOutstanding = (invoice, region) => {
  const credited = (invoice?.creditNotes || []).reduce(
    (total, note) => total + lineItemsTotal(note.items, region),
    0,
  )
  return Math.max(
    0,
    lineItemsTotal(invoice?.items, region) -
      credited -
      Number(invoice?.discount || 0) -
      Number(invoice?.totalPaid || 0),
  )
}

const UnpaidInvoices = () => {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const navigate = useNavigate()
  const theme = useTheme()
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const currency = orgConfig?.orgSettings?.currency ?? '£'
  const region = tenantInfo?.region

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    API.get('/unpaid-invoices')
      .then((res) => {
        if (res.status === 200) {
          setRows(res.data)
          setLoading(false)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack component={Paper} padding={1} minHeight={400} gap={1}>
      <Typography variant="title2">Unpaid Invoices</Typography>
      <Stack direction="row" gap={2}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="title3" fontSize="1rem" fontWeight="bold">
            Total Due:
          </Typography>
          <Typography variant="caption">
            {currency}
            {rows
              .reduce((accumulator, invoice) => {
                accumulator += getOutstanding(invoice, region)
                return accumulator
              }, 0)
              .toFixed(2)}
          </Typography>
        </Stack>
      </Stack>
      <CustomDataGrid
        rows={rows}
        loading={loading}
        getRowId={(params) => params._id}
        disableRowSelectionOnClick
        initialState={{
          columns: {
            columnVisibilityModel: {
              invRef: isMobile ? false : true,
              isSent: isMobile ? false : true
            }
          }
        }}
        sx={{
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
        onRowClick={({ row }) => navigate('/invoices/' + row._id)}
        slots={{
          noRowsOverlay: () => (
            <Box
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Alert severity={fetchError ? 'error' : 'success'}>
                {fetchError
                  ? 'Unable to fetch data right now. Please try again later.'
                  : 'No Unpaid Invoices'}
              </Alert>
            </Box>
          ),
        }}
        columns={[
          {
            field: 'invRef',
            headerName: 'Invoice Reference',
            renderCell: ({ row }) => row.reference,
            flex: 0.8,
          },
          {
            field: 'event',
            headerName: 'Event',
            renderCell: ({ row }) =>
              row.event?._id ? (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate('/events/' + row.event._id)
                  }}
                >
                  {row.event?.title || 'Event'}
                </Button>
              ) : (
                row.event?.title
              ),
            flex: 1,
          },
          {
            field: 'amount',
            headerName: 'Outstanding',
            renderCell: ({ row }) =>
              `${currency}${getOutstanding(row, region).toFixed(2)}`,
            flex: 0.6,
          },
          {
            field: 'isSent',
            headerName: 'Status',
            renderCell: ({ row }) => (
              <Chip
                size="small"
                label={row.status || 'Draft'}
                color={row.status === 'Overdue' ? 'error' : 'primary'}
              />
            ),
            flex: 0.5,
          },
        ]}
      />
    </Stack>
  )
}

export default UnpaidInvoices
